import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CaptureNeedService } from '@banyan/modules/capture-needs/services/capture-need.service';
import { SeoMetaService } from '@banyan/modules/shared/services/seo-meta/seo-meta.service';
import { TranslateService } from '@ngx-translate/core';
import { from, Subject, takeUntil } from 'rxjs';

@Component({
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
})
export class HomePageComponent implements OnInit, OnDestroy {
  @ViewChild('homeProductCategories', { read: ViewContainerRef })
  homeProductCategoriesTemplate!: ViewContainerRef;
  @ViewChild('homeExpertiseSection', { read: ViewContainerRef })
  homeExpertiseSectionTemplate!: ViewContainerRef;
  @ViewChild('homeArticlesSection', { read: ViewContainerRef })
  homeArticlesSectionTemplate!: ViewContainerRef;

  destroy = new Subject<void>();

  componentsAlreadyLoaded = false;
  componentsAreLoading = false;
  captureNeedService = inject(CaptureNeedService);

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.componentsAlreadyLoaded && !this.componentsAreLoading) {
        this.componentsAreLoading = true;
        this.loadComponents();
      }
    }
  }

  constructor(
    private readonly translateService: TranslateService,
    private readonly seoMetaService: SeoMetaService,
    @Inject(PLATFORM_ID) private readonly platformId: any,
  ) {
    this.captureNeedService.resetData();
    this.captureNeedService.getSegments();
  }

  ngOnInit(): void {
    this.updateSeoMetaData();
  }

  updateSeoMetaData(): void {
    this.translateService
      .get('seo.home')
      .pipe(takeUntil(this.destroy))
      .subscribe(translations => {
        this.seoMetaService.updateMetaData(
          translations.title,
          translations.description,
          translations.keywords,
        );
      });
  }

  loadComponents(): void {
    from(
      Promise.all([
        import(
          '../../../standalone/home-product-categories/home-product-categories.component'
          ),
        import(
          '../../../standalone/home-articles-section/home-articles-section.component'
          ),
        import(
          '../../../standalone/home-expertise-section/home-expertise-section.component'
          ),
      ]),
    )
      .subscribe(result => {
        const homeProductCategoriesComponent =
          result[0].HomeProductCategoriesComponent;
        this.homeProductCategoriesTemplate.createComponent(
          homeProductCategoriesComponent,
        );
        const homeArticlesSectionComponent =
          result[1].HomeArticlesSectionComponent;
        this.homeArticlesSectionTemplate.createComponent(
          homeArticlesSectionComponent,
        );
        const homeExpertiseSectionComponent =
          result[2].HomeExpertiseSectionComponent;
        this.homeExpertiseSectionTemplate.createComponent(
          homeExpertiseSectionComponent,
        );
        this.componentsAlreadyLoaded = true;
        this.componentsAreLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
